import React from 'react';

import { AddressForm } from '@oysterjs/ui/address';
import { Button } from '@oysterjs/ui/button';
import { Checkbox, CheckboxField, CheckboxGroup } from '@oysterjs/ui/checkbox';
import { Combobox } from '@oysterjs/ui/combobox';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle
} from '@oysterjs/ui/dialog';
import { Field, FieldGroup, Fieldset, Label } from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';

import { FormError } from './form-error';

import { messageOfFailure } from '@oysterjs/core/graphql/errors';

import {
  AddressInput,
  ClientFieldsFragment,
  useCreateClientMutation,
  useGetAgencyUsersQuery,
  useUpdateClientMutation
} from '~/api/graphql';

/** The generated input type, so the draft type-checks straight into the mutation. */
type DraftAddress = AddressInput;

type ClientDraft = {
  name: string;
  email: string;
  phone: string;
  address: DraftAddress;
  isCommercial: boolean;
  isPersonal: boolean;
  agencyUserOwnerId: string;
};

export const draftOf = (
  client?: ClientFieldsFragment | null,
  initialName?: string
): ClientDraft => ({
  name: client?.name ?? initialName ?? '',
  email: client?.email ?? '',
  phone: client?.phone ?? '',
  address: {
    line1: client?.address?.line1 ?? '',
    line2: client?.address?.line2 ?? '',
    city: client?.address?.city ?? '',
    zone: client?.address?.zone ?? '',
    postalCode: client?.address?.postalCode ?? ''
  },
  isCommercial: client?.isCommercial ?? false,
  isPersonal: client?.isPersonal ?? false,
  agencyUserOwnerId: client?.agencyUserOwner?.id ?? ''
});

/**
 * Sent only once some part is filled in: the server requires line1, city, state
 * and postal code together, and would reject a blank form's four empty strings.
 *
 * Emptying every box does not clear a stored address -- an omitted field is left
 * alone, and omitted is all the input can express.
 */
export const addressOf = (address: DraftAddress) => {
  const filled = [address.line1, address.line2, address.city, address.zone, address.postalCode];
  if (filled.every((part) => !part?.trim())) return undefined;
  return {
    line1: address.line1.trim(),
    line2: address.line2?.trim() || undefined,
    city: address.city.trim(),
    zone: address.zone.trim(),
    postalCode: address.postalCode.trim()
  };
};

/** An empty box means "unknown", which the schema spells as an absent field. */
const optional = (value: string) => value.trim() || undefined;

const SAVE_FAILED = 'Could not save this client. Nothing was changed.';

/** The owner box's value for "no owner", which no agency user id can collide with. */
const UNASSIGNED_OWNER = '__unassigned__';

type OwnerOption = { value: string; label: string; description?: string };

/**
 * The shared Combobox reports '' on every keystroke, so a search typed and then
 * abandoned would otherwise read as clearing the owner. Only a picked option
 * changes it, and leaving the box remounts it to show the owner still held.
 */
const OwnerCombobox = (props: {
  ownerId: string;
  options: OwnerOption[];
  disabled: boolean;
  onChange: (ownerId: string) => void;
}) => {
  const [remountKey, setRemountKey] = React.useState(0);
  const typed = React.useRef(false);
  return (
    <div
      data-slot="control"
      onBlur={(event) => {
        if (typed.current && !event.currentTarget.contains(event.relatedTarget)) {
          typed.current = false;
          setRemountKey((key) => key + 1);
        }
      }}
    >
      <Combobox
        key={remountKey}
        value={props.ownerId || UNASSIGNED_OWNER}
        disabled={props.disabled}
        minQueryLength={0}
        maxOptions={1000}
        options={props.options}
        onChange={(id) => {
          if (id === '') {
            typed.current = true;
            return;
          }
          typed.current = false;
          props.onChange(id === UNASSIGNED_OWNER ? '' : id);
        }}
      />
    </div>
  );
};

/**
 * Create and edit are the same eight fields: pass a client to edit it, pass none
 * to create one. Both lines start off on a new client, since not yet known is
 * the ordinary state.
 */
export const ClientFormDialog = ({
  open,
  client,
  initialName,
  onClose,
  onCreated
}: {
  open: boolean;
  client?: ClientFieldsFragment | null;
  /** Starting name for a new client. Ignored when editing an existing one. */
  initialName?: string;
  onClose: () => void;
  onCreated?: (client: ClientFieldsFragment) => void;
}) => {
  const [draft, setDraft] = React.useState<ClientDraft>(() => draftOf(client, initialName));
  const [error, setError] = React.useState<string | null>(null);

  const [createClient, { loading: creating }] = useCreateClientMutation();
  const [updateClient, { loading: updating }] = useUpdateClientMutation();
  // Mounted by the clients list, every client detail page and every workspace
  // top bar, so without skip it fetches users for a dropdown nobody opened.
  const { data: users } = useGetAgencyUsersQuery({ fetchPolicy: 'cache-first', skip: !open });
  const ownerOptions = React.useMemo<OwnerOption[]>(
    () => [
      { value: UNASSIGNED_OWNER, label: 'Unassigned' },
      ...(users?.agencyUsers ?? []).map((user) => ({
        value: user.id,
        label: user.name || user.email,
        description: user.name ? user.email : undefined
      }))
    ],
    [users?.agencyUsers]
  );

  // Reopening must not show the last client's half-finished edits.
  React.useEffect(() => {
    if (open) {
      setDraft(draftOf(client, initialName));
      setError(null);
    }
  }, [open, client, initialName]);

  const updateField = <K extends keyof ClientDraft>(field: K, value: ClientDraft[K]) =>
    setDraft((prev) => ({ ...prev, [field]: value }));

  const saving = creating || updating;
  const nameGiven = draft.name.trim().length > 0;

  const submit = async () => {
    if (!nameGiven || saving) return;
    setError(null);

    const fields = {
      name: draft.name.trim(),
      email: optional(draft.email),
      phone: optional(draft.phone),
      address: addressOf(draft.address),
      isCommercial: draft.isCommercial,
      isPersonal: draft.isPersonal,
      agencyUserOwnerId: optional(draft.agencyUserOwnerId)
    };

    if (client) {
      const result = await updateClient({ variables: { id: client.id, input: fields } });
      const message = messageOfFailure(result, SAVE_FAILED);
      if (message) {
        setError(message);
        return;
      }
      onClose();
      return;
    }

    const result = await createClient({ variables: { input: fields } });
    const message = messageOfFailure(result, SAVE_FAILED);
    if (message) {
      setError(message);
      return;
    }
    if (result.data?.createClient) onCreated?.(result.data.createClient);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} size="2xl">
      <DialogTitle>{client ? 'Edit client' : 'New client'}</DialogTitle>
      <DialogDescription>
        {client
          ? 'Changes apply everywhere this client appears.'
          : 'A name is enough to start. Everything else can be filled in later.'}
      </DialogDescription>
      <DialogBody>
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Name</Label>
              <Input
                autoFocus
                value={draft.name}
                disabled={saving}
                onChange={(e) => updateField('name', e.currentTarget.value)}
              />
            </Field>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <Field>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={draft.email}
                  disabled={saving}
                  onChange={(e) => updateField('email', e.currentTarget.value)}
                />
              </Field>
              <Field>
                <Label>Phone</Label>
                <Input
                  type="tel"
                  value={draft.phone}
                  disabled={saving}
                  onChange={(e) => updateField('phone', e.currentTarget.value)}
                />
              </Field>
            </div>
            <Field>
              <Label>Owner</Label>
              <OwnerCombobox
                ownerId={draft.agencyUserOwnerId}
                disabled={saving}
                options={ownerOptions}
                onChange={(id) => updateField('agencyUserOwnerId', id)}
              />
            </Field>
          </FieldGroup>

          <FieldGroup>
            <Field>
              <Label>Client type</Label>
              <CheckboxGroup>
                <CheckboxField>
                  <Checkbox
                    checked={draft.isCommercial}
                    disabled={saving}
                    onChange={(isCommercial) => updateField('isCommercial', isCommercial)}
                    color="sky"
                  />
                  <Label>Commercial lines</Label>
                </CheckboxField>
                <CheckboxField>
                  <Checkbox
                    checked={draft.isPersonal}
                    disabled={saving}
                    onChange={(isPersonal) => updateField('isPersonal', isPersonal)}
                    color="sky"
                  />
                  <Label>Personal lines</Label>
                </CheckboxField>
              </CheckboxGroup>
            </Field>
          </FieldGroup>

          <div className="pt-6">
            <AddressForm
              addressTitle="Address"
              disabled={saving}
              initialValue={draft.address}
              onChange={(update) =>
                setDraft((prev) => ({ ...prev, address: update(prev.address) }))
              }
            />
          </div>
        </Fieldset>

        <FormError message={error} className="mt-4 text-sm text-red-600 dark:text-red-400" />
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button color="sky" onClick={submit} disabled={!nameGiven || saving}>
          {client ? 'Save' : 'Create client'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
